import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createHabit,
  getHabitLogs,
  getHabits,
  logHabit,
  type Habit,
  type HabitLog,
  type HabitPeriod
} from '../api/habits';
import '../styles/habits.scss';

const MAX_WEEKS_ON_STRIP = 20;
const GRID_WEEKS = 9;

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isoWeekKey(d: Date): string {
  // ISO week, Monday-based - matches how the server keys weekly periods.
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function periodKeyOf(period: HabitPeriod, d: Date): string {
  if (period === 'DAY') return dayKey(d);
  if (period === 'WEEK') return isoWeekKey(d);
  return monthKey(d);
}

/**
 * Columns of 7 days, oldest first - the same rhythm as the check-in timeline.
 * The window is shared by every row so the strips line up and can be compared,
 * but it never reaches further back than the oldest habit: a fixed 20 weeks
 * would leave most of the grid as dead cells from before anything was tracked.
 */
function weekColumns(earliestStart: string | null): Date[][] {
  const today = new Date();
  const end = new Date(today);
  // Finish the current week so today sits in the last column.
  end.setDate(today.getDate() + (7 - (today.getDay() || 7)));

  let weeks = MAX_WEEKS_ON_STRIP;
  if (earliestStart) {
    const start = new Date(earliestStart + 'T12:00:00');
    const spanDays = Math.ceil((end.getTime() - start.getTime()) / 86400000);
    weeks = Math.max(4, Math.min(MAX_WEEKS_ON_STRIP, Math.ceil(spanDays / 7)));
  }

  const days: Date[] = [];
  for (let i = weeks * 7 - 1; i >= 0; i -= 1) {
    const d = new Date(end);
    d.setDate(end.getDate() - i);
    days.push(d);
  }
  const cols: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) cols.push(days.slice(i, i + 7));
  return cols;
}

interface CellState {
  cls: string;
  title: string;
}

function cellFor(habit: Habit, day: Date, logsByDate: Map<string, HabitLog>, satisfiedPeriods: Set<string>): CellState {
  const key = dayKey(day);
  const today = new Date();
  const isFuture = day > today;
  const beforeStart = key < habit.startDate;
  if (isFuture || beforeStart) {
    return { cls: 'hb-cell out', title: '' };
  }
  const log = logsByDate.get(key);
  const success = log ? (habit.polarity === 'NEGATIVE' ? !log.occurred : log.occurred) : null;
  const periodSatisfied = satisfiedPeriods.has(periodKeyOf(habit.period, day));

  if (success === true) {
    // For a habit that tracks a number, the amount shades the cell: one beer and
    // ten both break a streak, but they should not look identical.
    return { cls: 'hb-cell hit', title: `${key}${log?.note ? ' - ' + log.note : ''}` };
  }
  if (success === false) {
    const amt = log?.amount ?? null;
    const strength = amt == null ? 2 : Math.min(4, Math.max(1, Math.ceil(amt / 3)));
    return {
      cls: `hb-cell miss m${strength}`,
      title: `${key}${amt != null ? ` - ${amt}${habit.unit ? ' ' + habit.unit : ''}` : ''}${log?.note ? ' - ' + log.note : ''}`
    };
  }
  // No entry: for a multi-day period that already counts as done, the day still
  // belongs to a good week - shown as a wash rather than a gap.
  if (habit.period !== 'DAY' && periodSatisfied) {
    return { cls: 'hb-cell wash', title: key };
  }
  return { cls: 'hb-cell empty', title: key };
}

function HabitRow({
  habit,
  index,
  windowStart,
  view
}: {
  habit: Habit;
  index: number;
  windowStart: string | null;
  view: ViewMode;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const logs = useQuery({
    queryKey: ['habit-logs', habit.id],
    queryFn: () => getHabitLogs(habit.id)
  });

  const mark = useMutation({
    mutationFn: (v: { onDate: string; occurred: boolean; amount?: number | null }) =>
      logHabit(habit.id, v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['habits'] });
      queryClient.invalidateQueries({ queryKey: ['habit-logs', habit.id] });
    }
  });

  const logsByDate = useMemo(() => {
    const m = new Map<string, HabitLog>();
    (logs.data ?? []).forEach((l) => m.set(l.onDate, l));
    return m;
  }, [logs.data]);

  const satisfiedPeriods = useMemo(() => {
    const counts = new Map<string, number>();
    (logs.data ?? []).forEach((l) => {
      const success = habit.polarity === 'NEGATIVE' ? !l.occurred : l.occurred;
      if (!success) return;
      const key = periodKeyOf(habit.period, new Date(l.onDate + 'T12:00:00'));
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    const out = new Set<string>();
    counts.forEach((n, key) => {
      if (n >= habit.targetCount) out.add(key);
    });
    return out;
  }, [logs.data, habit.period, habit.polarity, habit.targetCount]);

  // A tile carries a shorter history than a full-width row: enough to read the
  // shape of the last weeks without forcing the tile wide.
  const cols = useMemo(() => {
    const all = weekColumns(windowStart);
    return view === 'grid' ? all.slice(-GRID_WEEKS) : all;
  }, [windowStart, view]);
  const today = dayKey(new Date());
  const yesterday = dayKey(new Date(Date.now() - 86400000));
  const askOccurred = habit.polarity === 'NEGATIVE';

  return (
    <article className="hb-row" style={{ animationDelay: `${Math.min(index, 12) * 45}ms` }}>
      <header className="hb-row-head">
        <button className="hb-name" onClick={() => setOpen((v) => !v)}>
          {habit.icon && <span className="hb-icon">{habit.icon}</span>}
          <span>{habit.name}</span>
          <span className="hb-cadence">
            {habit.period === 'DAY'
              ? t('habits.daily')
              : habit.targetCount > 1
                ? t('habits.timesPerWeek', { count: habit.targetCount })
                : t('habits.weekly')}
            {habit.expectedDays ? ` · ${habit.expectedDays.toLowerCase()}` : ''}
          </span>
        </button>

        <div className="hb-quick">
          <span className="hb-quick-label">{t('habits.yesterday')}</span>
          <button
            className="hb-tick yes"
            title={askOccurred ? t('habits.wasIt') : t('habits.didIt')}
            onClick={() => mark.mutate({ onDate: yesterday, occurred: !askOccurred })}
          >
            ✓
          </button>
          <button
            className="hb-tick no"
            onClick={() => mark.mutate({ onDate: yesterday, occurred: askOccurred })}
          >
            ✕
          </button>
          <span className="hb-quick-label">{t('habits.today')}</span>
          <button
            className="hb-tick yes"
            onClick={() => mark.mutate({ onDate: today, occurred: !askOccurred })}
          >
            ✓
          </button>
          <button
            className="hb-tick no"
            onClick={() => mark.mutate({ onDate: today, occurred: askOccurred })}
          >
            ✕
          </button>
        </div>
      </header>

      <div className="hb-body">
        <div className="hb-figures">
          <div className="hb-streak">
            <span className="hb-streak-num">{habit.currentStreak}</span>
            <span className="hb-streak-unit">
              {habit.period === 'DAY' ? t('habits.days') : t('habits.weeks')}
            </span>
          </div>
          <dl className="hb-meta">
            <div>
              <dt>{t('habits.best')}</dt>
              <dd>{habit.longestStreak}</dd>
            </div>
            <div>
              <dt>{t('habits.rate')}</dt>
              <dd>{Math.round(habit.rate * 100)}%</dd>
            </div>
          </dl>
        </div>

        <div className="hb-grid" aria-label={habit.name}>
          {cols.map((col) => (
            <div className="hb-col" key={dayKey(col[0])}>
              {col.map((day) => {
                const c = cellFor(habit, day, logsByDate, satisfiedPeriods);
                return <span key={dayKey(day)} className={c.cls} title={c.title} />;
              })}
            </div>
          ))}
        </div>
      </div>

      {open && (
        <div className="hb-detail">
          {(logs.data ?? []).length === 0 && <p className="hb-muted">{t('habits.noLogs')}</p>}
          <ul className="hb-loglist">
            {(logs.data ?? [])
              .slice()
              .reverse()
              .slice(0, 20)
              .map((l) => {
                const success = habit.polarity === 'NEGATIVE' ? !l.occurred : l.occurred;
                return (
                  <li key={l.id}>
                    <span className="hb-log-date">{l.onDate}</span>
                    <span className={`hb-log-dot ${success ? 'ok' : 'bad'}`} />
                    {l.amount != null && (
                      <span className="hb-log-amount">
                        {l.amount}
                        {habit.unit ? ` ${habit.unit}` : ''}
                      </span>
                    )}
                    {l.note && <span className="hb-log-note">{l.note}</span>}
                  </li>
                );
              })}
          </ul>
        </div>
      )}
    </article>
  );
}

type ViewMode = 'grid' | 'list';

const VIEW_KEY = 'msb.habits.view';

export function HabitsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [period, setPeriod] = useState<HabitPeriod>('DAY');
  const [negative, setNegative] = useState(false);
  // Grid by default: the point is seeing every habit at once, not scrolling
  // through them. The choice is a device preference, so it lives locally.
  const [view, setView] = useState<ViewMode>(
    () => (localStorage.getItem(VIEW_KEY) as ViewMode) || 'grid'
  );
  const chooseView = (v: ViewMode) => {
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
  };

  const habits = useQuery({ queryKey: ['habits'], queryFn: getHabits });

  const add = useMutation({
    mutationFn: () =>
      createHabit({
        name: name.trim(),
        period,
        polarity: negative ? 'NEGATIVE' : 'POSITIVE'
      }),
    onSuccess: () => {
      setName('');
      queryClient.invalidateQueries({ queryKey: ['habits'] });
    }
  });

  const rows = habits.data ?? [];
  const alive = rows.filter((h) => h.currentStreak > 0).length;
  // One window for every row, anchored at the oldest habit.
  const windowStart = useMemo(
    () => rows.reduce<string | null>((min, h) => (!min || h.startDate < min ? h.startDate : min), null),
    [rows]
  );

  return (
    <section className={`hb hb-mode-${view}`}>
      <header className="hb-head">
        <h2 className="hb-title">{t('habits.title')}</h2>
        {rows.length > 0 && (
          <span className="hb-tally">
            {t('habits.alive', { alive, total: rows.length })}
          </span>
        )}
        <div className="hb-view" role="group" aria-label={t('habits.view')}>
          <button
            className={view === 'grid' ? 'on' : ''}
            onClick={() => chooseView('grid')}
          >
            {t('habits.viewGrid')}
          </button>
          <button
            className={view === 'list' ? 'on' : ''}
            onClick={() => chooseView('list')}
          >
            {t('habits.viewList')}
          </button>
        </div>
      </header>

      <form
        className="hb-add"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) add.mutate();
        }}
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('habits.placeholder')}
        />
        <select value={period} onChange={(e) => setPeriod(e.target.value as HabitPeriod)}>
          <option value="DAY">{t('habits.daily')}</option>
          <option value="WEEK">{t('habits.weekly')}</option>
          <option value="MONTH">{t('habits.monthly')}</option>
        </select>
        <label className="hb-neg">
          <input type="checkbox" checked={negative} onChange={(e) => setNegative(e.target.checked)} />
          <span>{t('habits.negative')}</span>
        </label>
        <button type="submit" disabled={!name.trim() || add.isPending}>
          {t('habits.add')}
        </button>
      </form>

      {rows.length === 0 ? (
        <p className="hb-muted hb-empty">{t('habits.empty')}</p>
      ) : (
        <div className="hb-list">
          {rows.map((h, i) => (
            <HabitRow habit={h} index={i} windowStart={windowStart} view={view} key={h.id} />
          ))}
        </div>
      )}

      <div className="hb-legend">
        <span className="hb-cell hit" /> {t('habits.legendHit')}
        <span className="hb-cell miss m2" /> {t('habits.legendMiss')}
        <span className="hb-cell wash" /> {t('habits.legendWash')}
        <span className="hb-cell empty" /> {t('habits.legendEmpty')}
      </div>
    </section>
  );
}
